import type { VorankuendigungData } from '../types';
import type { Angabe, ProjektSteckbrief } from './projektSteckbrief/steckbrief';
import { GRUND } from './projektSteckbrief/steckbrief';
import { labelFuerId, nummerFuerId, type DangerousWorkId } from '../data/anhang2Bruecke';
import { filledParties, markManualField } from './vorankuendigungModel';
import { parseDateLocal } from './dateService';

/**
 * Leiste „Aus der Planungsphase" der Vorankündigung (Plan Planungsphase,
 * Stufe 3.1) — reine Funktionen, ohne DOM und ohne Supabase.
 *
 * Der Projektsteckbrief (`services/projektSteckbrief`) liefert je Angabe
 * Wert, Quelle und Stand. Hier wird daraus je Punkt der Vorankündigung EIN
 * Vorschlag mit dem Unterschied zum aktuellen Wert, oder ein Hinweis.
 *
 * Regeln:
 *  - Ein Vorschlag zum Übernehmen entsteht nur aus einer Angabe mit Stand
 *    `belegt`. Alles andere (`ungeklaert`, Rückfallquelle) ist höchstens ein
 *    Hinweis „in der Planungsphase noch offen" — und auch der nur, solange das
 *    Feld der Vorankündigung leer ist. Ein gefülltes Feld ohne Gegenstück in
 *    der Planungsphase bekommt keinen Hinweis: dort gäbe es nichts zu tun.
 *  - Steht der Planungswert schon im Feld (nach Normalisierung: Leerraum,
 *    Datumsschreibweise, Zahl, Reihenfolge der Anhang-II-Ids), gibt es keine
 *    Zeile. Übernommene Vorschläge verschwinden damit von selbst.
 *  - Nichts hier schreibt. `wendeUebernahmeAn` baut den neuen Datensatz, den
 *    die Ansicht per `setData` setzt — derselbe Weg wie eine Eingabe. Punkt 7
 *    und 9 werden dabei als von Hand gesetzt markiert (`manualFields`): die
 *    Übernahme ist eine bewusste Handlung, genau wie eine Eingabe im Feld.
 *  - Fehlt die Planungsphase ganz, steht das EINMAL in der Leiste, nicht an
 *    jedem Punkt noch einmal.
 *
 * Fragen (`fragen.vorankuendigung`): Zu Punkt 6, 7 und 9 gehören Katalog-
 * fragen (PL-B-03, PL-A-11, PL-B-10). Eine Übernahme nennt deren Codes,
 * damit die Ansicht `uebernommen_am` stempeln kann — aber nur für Fragen,
 * die beantwortet sind: an einer offenen Frage wäre „übernommen" eine
 * Erkenntnis, die es noch nicht gibt.
 *
 * Nicht hier: Punkt 1/2 (Abgleich mit den Projektdaten,
 * `vorankuendigungAbgleich.ts`), Punkt 5 (`vorankuendigungKoordinatoren.ts`),
 * Punkt 8/10 (Verteiler, Abgleich-Effekt in `VorankuendigungView.tsx`).
 */

// ─── Typen ──────────────────────────────────────────────────────────────────

/** Was eine Übernahme am Datensatz setzt. */
export type VorankUebernahme =
    | { feld: 'projectDescription' | 'startDate' | 'endDate'; wert: string }
    /** Punkte 7 und 9 — werden mit der Übernahme als von Hand gesetzt markiert. */
    | { feld: 'maxWorkers' | 'numContractors'; wert: string }
    | { feld: 'selectedDangerousWorks'; wert: DangerousWorkId[] };

export type VorankVorschlagArt = 'uebernahme' | 'hinweis';

export interface VorankVorschlag {
    /** Eindeutig je Zeile — Schlüssel für „Ausblenden" in der Sitzung. */
    id: string;
    /** Beschriftung, z. B. „Punkt 7 – Max. Beschäftigte". */
    punkt: string;
    art: VorankVorschlagArt;
    /** Herkunft, Wortlaut aus dem Steckbrief (`Angabe.quelle`). */
    quelle: string;
    /** Was heute in der Vorankündigung steht — lesbar; ein leeres Feld wird benannt. Fehlt bei der Sammelzeile. */
    aktuell?: string;
    /** Bei `uebernahme` der Planungs-/Projektwert lesbar, bei `hinweis` der Hinweissatz. */
    planung: string;
    /** Nur bei `uebernahme`. */
    uebernahme?: VorankUebernahme;
    /** Codes beantworteter, noch nicht gestempelter Fragen mit Zielobjekt Vorankündigung. */
    codes: string[];
}

export const AKTUELL_LEER = 'nicht ausgefüllt';

/** Katalogfragen je Punkt — nur Fragen mit Zielobjekt Vorankündigung. */
const FRAGEN_JE_PUNKT = {
    P6_BEGINN: ['PL-B-03'],
    P7: ['PL-A-11'],
    P9: ['PL-B-10'],
} as const;

const QUELLE_FRAGE = (code: string): string => `Planungsphase · Fragen (${code})`;

// ─── Normalisierung ─────────────────────────────────────────────────────────

const textNorm = (s: string | null | undefined): string => (s || '').replace(/\s+/g, ' ').trim();

const gleichText = (a: string | null | undefined, b: string | null | undefined): boolean =>
    textNorm(a).toLocaleLowerCase('de') === textNorm(b).toLocaleLowerCase('de');

/** Ganze Zahl aus einem Textfeld — `null` bei leer oder Freitext („ca. 25"). */
const ganzzahl = (s: string | null | undefined): number | null => {
    const m = (s || '').trim().match(/^(\d+)$/);
    return m ? Number(m[1]) : null;
};

/**
 * Datum eines Vorankündigungsfelds als ISO-Tag. Die Felder führen
 * TT.MM.JJJJ (so füllt sie der Abgleich-Effekt), Altstände können ISO
 * enthalten. Reines Zerlegen, `parseDateLocal` prüft nur die Plausibilität.
 */
const isoAusFeld = (roh: string | null | undefined): string | null => {
    const s = (roh || '').trim();
    const de = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (de) {
        const tag = `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
        return parseDateLocal(tag) ? tag : null;
    }
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return parseDateLocal(s) ? s : null;
    return null;
};

/** ISO-Tag → TT.MM.JJJJ durch Zerlegen; leer bei unbrauchbarer Eingabe. */
export const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/** Erster Absatz, Leerraum zusammengezogen — Punkt 3 ist eine kurze Angabe. */
const ersterAbsatz = (text: string): string => textNorm(text.split(/\n\s*\n/)[0]);

const gleicheMenge = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every(x => b.includes(x)) && b.every(x => a.includes(x));

/** „Nr. 4: Arbeiten in geringerem Abstand …" — nie eine nackte Id, aber auch keine Lüge bei unbekannter Id. */
const anhangLabel = (id: string): string => {
    const nummer = nummerFuerId(id);
    const label = labelFuerId(id);
    if (nummer === null || !label) return `unbekannte Id „${id}"`;
    return `Nr. ${nummer}: ${label}`;
};

/** Beschreibungstext zu Punkt 11 — dasselbe Format, das `FormEditor` beim Ankreuzen erzeugt. */
export const anhang2Beschreibung = (ids: readonly string[]): string => {
    const labels = ids.map(labelFuerId).filter((l): l is string => Boolean(l));
    return labels.length ? `- ${labels.join('\n- ')}` : '';
};

// ─── Zeilen bauen ───────────────────────────────────────────────────────────

const offen = (a: Angabe<unknown>): string =>
    a.grund ? `In der Planungsphase noch offen — ${a.grund}` : 'In der Planungsphase noch offen.';

const uebernahme = (
    id: string, punkt: string, quelle: string, aktuell: string, planung: string,
    was: VorankUebernahme, codes: readonly string[],
): VorankVorschlag => ({ id, punkt, art: 'uebernahme', quelle, aktuell: aktuell || AKTUELL_LEER, planung, uebernahme: was, codes: [...codes] });

const hinweis = (id: string, punkt: string, quelle: string, aktuell: string, planung: string): VorankVorschlag =>
    ({ id, punkt, art: 'hinweis', quelle, aktuell: aktuell || AKTUELL_LEER, planung, codes: [] });

/**
 * Codes beantworteter Fragen mit Zielobjekt Vorankündigung, die noch nicht
 * als übernommen gestempelt sind. Nur diese darf die Ansicht stempeln.
 */
export const stempelbareCodes = (sb: ProjektSteckbrief, codes: readonly string[]): string[] =>
    sb.fragen.vorankuendigung
        .filter(f => codes.includes(f.code) && f.status === 'beantwortet' && !f.uebernommen_am)
        .map(f => f.code);

/** Antwort einer beantworteten Frage mit Zielobjekt Vorankündigung, sonst `null`. */
const antwortAuf = (sb: ProjektSteckbrief, code: string): string | null => {
    const f = sb.fragen.vorankuendigung.find(x => x.code === code);
    if (!f || f.status !== 'beantwortet') return null;
    const t = textNorm(f.antwort);
    return t || null;
};

/** Ergebnis eines Punkts: Zeile, nichts, oder „ohne Planungsphase nicht beurteilbar". */
type PunktErgebnis = VorankVorschlag | null | 'phase_fehlt';

/** Punkt 3 – Art des Bauvorhabens ← Bauaufgabe, sonst erster Absatz der Baubeschreibung. */
const punkt3 = (sb: ProjektSteckbrief, data: VorankuendigungData, phasenGrund: string | null): PunktErgebnis => {
    const punkt = 'Punkt 3 – Art des Bauvorhabens';
    const quelle = sb.bauaufgabe.stand === 'belegt' ? sb.bauaufgabe : sb.baubeschreibung;
    const aktuell = textNorm(data.projectDescription);
    if (quelle.stand === 'belegt' && quelle.wert) {
        const neu = ersterAbsatz(quelle.wert);
        if (!neu || gleichText(neu, aktuell)) return null;
        return uebernahme('P3', punkt, quelle.quelle, aktuell, neu, { feld: 'projectDescription', wert: neu }, []);
    }
    if (aktuell) return null;
    if (phasenGrund) return 'phase_fehlt';
    return hinweis('P3', punkt, quelle.quelle, aktuell, offen(quelle));
};

/** Punkt 4 – Dritter nach § 4: nur Hinweis, wenn die Planungsphase einen nennt, der in der Liste fehlt. */
const punkt4 = (sb: ProjektSteckbrief, data: VorankuendigungData): VorankVorschlag | null => {
    const name = sb.dritter.name;
    if (name.stand !== 'belegt' || !name.wert) return null;
    const parteien = filledParties(data.responsibleThirdParties);
    if (parteien.some(p => gleichText(p.name, name.wert))) return null;
    const rolle = sb.dritter.rolle.stand === 'belegt' ? sb.dritter.rolle.wert : null;
    const anschrift = sb.dritter.anschrift.stand === 'belegt' ? sb.dritter.anschrift.wert : null;
    const wer = `${name.wert}${rolle ? ` (${rolle})` : ''}${anschrift ? `, ${textNorm(anschrift)}` : ''}`;
    return hinweis(
        'P4',
        'Punkt 4 – Verantwortlicher Dritter (§ 4 BaustellV)',
        name.quelle,
        parteien.map(p => textNorm(p.name)).filter(Boolean).join('; '),
        `Die Planungsphase nennt ${wer} — in Punkt 4 nicht aufgeführt. Dort als Eintrag ergänzen.`,
    );
};

/**
 * Punkt 6 – Baubeginn / Bauende ← Ausführungszeitraum (E1). Der Rückfall auf
 * Projektstart/-ende ist im Steckbrief `ungeklaert` mit Wert: kein Vorschlag,
 * aber ein Hinweis mit sichtbarer Quelle, wenn er vom Feld abweicht.
 */
const punkt6 = (sb: ProjektSteckbrief, data: VorankuendigungData, welche: 'beginn' | 'ende'): VorankVorschlag | null => {
    const a = sb.ausfuehrung[welche];
    const feld = welche === 'beginn' ? 'startDate' : 'endDate';
    const id = welche === 'beginn' ? 'P6.beginn' : 'P6.ende';
    const punkt = welche === 'beginn' ? 'Punkt 6 – Baubeginn' : 'Punkt 6 – Bauende';
    const aktuellRoh = (data[feld] || '').trim();
    const aktuellIso = isoAusFeld(aktuellRoh);
    const codes = welche === 'beginn' ? stempelbareCodes(sb, FRAGEN_JE_PUNKT.P6_BEGINN) : [];

    if (a.stand === 'belegt' && a.wert) {
        if (aktuellIso === a.wert) return null;
        const neu = tagDeutsch(a.wert);
        return uebernahme(id, punkt, a.quelle, aktuellRoh, neu, { feld, wert: neu }, codes);
    }
    if (a.wert) {
        // Rückfallquelle (Projektlaufzeit): der Abgleich-Effekt füllt leere
        // Felder damit — steht sie schon drin, gibt es nichts zu sagen.
        if (aktuellIso === a.wert) return null;
        return hinweis(id, punkt, a.quelle, aktuellRoh,
            `${tagDeutsch(a.wert)} nur als Rückfall, kein Vorschlag — ${a.grund || GRUND.rueckfallLaufzeit}`);
    }
    if (aktuellRoh) return null;
    return hinweis(id, punkt, a.quelle, aktuellRoh, `Noch offen — ${a.grund || GRUND.nichtGepflegt}`);
};

/** Punkt 7 – Höchstzahl Beschäftigte ← Merkmale (E2: die Planungsphase besitzt die Zahl). */
const punkt7 = (sb: ProjektSteckbrief, data: VorankuendigungData, phasenGrund: string | null): PunktErgebnis => {
    const punkt = 'Punkt 7 – Max. Beschäftigte';
    const a = sb.umfang.maxBeschaeftigte;
    const aktuellRoh = (data.maxWorkers || '').trim();
    if (a.stand === 'belegt' && a.wert !== null) {
        if (ganzzahl(aktuellRoh) === a.wert) return null;
        const neu = String(a.wert);
        return uebernahme('P7', punkt, a.quelle, aktuellRoh, neu, { feld: 'maxWorkers', wert: neu }, stempelbareCodes(sb, FRAGEN_JE_PUNKT.P7));
    }
    if (aktuellRoh) return null;
    if (phasenGrund) return 'phase_fehlt';
    return hinweis('P7', punkt, a.quelle, aktuellRoh, offen(a));
};

/**
 * Punkt 9 – Unternehmer ohne Beschäftigte. Das Feld erwartet eine Zahl
 * (Anhang I BaustellV), das Merkmal ist Ja/Nein: Nein → „0" als Vorschlag;
 * Ja → die Zahl kommt aus der Antwort auf PL-B-10, wenn sie eine ganze Zahl
 * ist, sonst nur ein Hinweis.
 */
const punkt9 = (sb: ProjektSteckbrief, data: VorankuendigungData, phasenGrund: string | null): PunktErgebnis => {
    const punkt = 'Punkt 9 – Unternehmer ohne Beschäftigte';
    const a = sb.umfang.unternehmerOhneBeschaeftigte;
    const aktuellRoh = (data.numContractors || '').trim();
    const aktuellZahl = ganzzahl(aktuellRoh);
    const codes = stempelbareCodes(sb, FRAGEN_JE_PUNKT.P9);

    if (a.stand === 'belegt' && a.wert === false) {
        if (aktuellZahl === 0) return null;
        return uebernahme('P9', punkt, a.quelle, aktuellRoh, 'keine (0)', { feld: 'numContractors', wert: '0' }, codes);
    }
    if (a.stand === 'belegt' && a.wert === true) {
        const antwort = antwortAuf(sb, 'PL-B-10');
        const zahl = ganzzahl(antwort);
        if (zahl !== null && zahl > 0) {
            if (aktuellZahl === zahl) return null;
            return uebernahme('P9', punkt, QUELLE_FRAGE('PL-B-10'), aktuellRoh, String(zahl), { feld: 'numContractors', wert: String(zahl) }, codes);
        }
        // Ja, aber ohne Zahl: nur melden, solange Punkt 9 leer oder „0" ist.
        if (aktuellRoh && aktuellZahl !== 0) return null;
        return hinweis('P9', punkt, a.quelle, aktuellRoh, antwort
            ? `Ja — Antwort auf PL-B-10: „${antwort}". Die Zahl in Punkt 9 eintragen.`
            : 'Ja — die Anzahl ist in der Planungsphase (PL-B-10) noch nicht beantwortet.');
    }
    if (aktuellRoh) return null;
    if (phasenGrund) return 'phase_fehlt';
    return hinweis('P9', punkt, a.quelle, aktuellRoh, offen(a));
};

/** Punkt 11 – Arbeiten nach Anhang II ← Live-Prüfung über die Anhang-II-Brücke. */
const punkt11 = (sb: ProjektSteckbrief, data: VorankuendigungData, phasenGrund: string | null): PunktErgebnis => {
    const punkt = 'Punkt 11 – Arbeiten nach Anhang II';
    const a = sb.anhang2.nummern;
    const soll = [...sb.anhang2.vorankuendigungIds];
    const ist = data.selectedDangerousWorks || [];
    const aktuell = ist.map(anhangLabel).join('; ');

    if (a.stand === 'belegt') {
        if (gleicheMenge(soll, ist)) return null;
        const sollMenge = new Set<string>(soll);
        const hinzu = soll.filter(id => !ist.includes(id)).map(anhangLabel);
        const weg = ist.filter(id => !sollMenge.has(id)).map(anhangLabel);
        const teile: string[] = [];
        if (hinzu.length) teile.push(`hinzu: ${hinzu.join('; ')}`);
        if (weg.length) teile.push(`entfällt: ${weg.join('; ')}`);
        const planung = soll.length
            ? `${soll.map(anhangLabel).join('; ')} (${teile.join(' · ')})`
            : `keine Arbeiten nach Anhang II (${teile.join(' · ')})`;
        return uebernahme('P11', punkt, a.quelle, aktuell, planung, { feld: 'selectedDangerousWorks', wert: soll }, []);
    }
    if (ist.length || data.hasDangerousWork) return null;
    if (phasenGrund) return 'phase_fehlt';
    const bisher = (a.wert || []).length ? `Bisher bejaht: Nr. ${(a.wert || []).join(', ')}. ` : '';
    return hinweis('P11', punkt, a.quelle, aktuell, `${bisher}${offen(a)}`);
};

// ─── Öffentliche Funktionen ─────────────────────────────────────────────────

/**
 * Alle Zeilen der Leiste, in der Reihenfolge der Punkte. Reiner Vergleich —
 * die Ansicht ruft das beim Rendern auf, nichts wird gesetzt.
 */
export const vorankuendigungVorschlaege = (sb: ProjektSteckbrief, data: VorankuendigungData): VorankVorschlag[] => {
    const phasenGrund = sb.planungsphase.vorhanden
        ? null
        : (sb.lesefehler.planungsphase ? GRUND.phaseUnlesbar(sb.lesefehler.planungsphase) : GRUND.keinePhase);

    const zeilen: VorankVorschlag[] = [];
    const ohnePhase: string[] = [];
    const nimm = (ergebnis: PunktErgebnis, kurz: string) => {
        if (ergebnis === 'phase_fehlt') ohnePhase.push(kurz);
        else if (ergebnis) zeilen.push(ergebnis);
    };

    nimm(punkt3(sb, data, phasenGrund), 'Punkt 3');
    nimm(punkt4(sb, data), 'Punkt 4');
    nimm(punkt6(sb, data, 'beginn'), 'Punkt 6');
    nimm(punkt6(sb, data, 'ende'), 'Punkt 6');
    nimm(punkt7(sb, data, phasenGrund), 'Punkt 7');
    nimm(punkt9(sb, data, phasenGrund), 'Punkt 9');
    nimm(punkt11(sb, data, phasenGrund), 'Punkt 11');

    if (phasenGrund && ohnePhase.length) {
        zeilen.push({
            id: 'planungsphase',
            punkt: 'Planungsphase',
            art: 'hinweis',
            quelle: 'Planungsphase',
            planung: `${phasenGrund} Ohne sie keine Vorschläge für ${ohnePhase.join(', ')}.`,
            codes: [],
        });
    }
    return zeilen;
};

/**
 * Den Datensatz nach einer Übernahme — die Ansicht setzt ihn per
 * `setData(prev => wendeUebernahmeAn(prev, …))`, damit `manualFields` vom
 * jeweils aktuellen Stand ausgeht.
 */
export const wendeUebernahmeAn = (data: VorankuendigungData, u: VorankUebernahme): VorankuendigungData => {
    switch (u.feld) {
        case 'projectDescription':
        case 'startDate':
        case 'endDate':
            return { ...data, [u.feld]: u.wert };
        case 'maxWorkers':
        case 'numContractors':
            // Bewusste Handlung wie eine Eingabe im Feld: der Abgleich mit den
            // Projektdaten fasst das Feld danach nicht mehr an.
            return { ...data, [u.feld]: u.wert, manualFields: markManualField(data, u.feld) };
        case 'selectedDangerousWorks':
            return {
                ...data,
                selectedDangerousWorks: [...u.wert],
                hasDangerousWork: u.wert.length > 0,
                dangerousWorkDescription: anhang2Beschreibung(u.wert),
            };
    }
};

/** Nur Zeilen mit Übernahme-Knopf. */
export const uebernehmbareVorschlaege = (liste: readonly VorankVorschlag[]): VorankVorschlag[] =>
    liste.filter(v => v.art === 'uebernahme');
